import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LicenseStatus } from '@shared/license-types'
import { useLicense } from '../contexts/LicenseContext'
import { useApi } from '../contexts/ApiContext'

interface Props {
  status: LicenseStatus
}

export default function LicenseExpiredModal({ status }: Props) {
  const [dismissed, setDismissed] = useState(false)
  const navigate = useNavigate()
  const { refresh } = useLicense()
  const api = useApi()

  if (!status.isExpired || !status.isActivated || dismissed) return null

  const isTrial = status.licenseType === 'trial'

  const handleReactivate = async () => {
    await api.resetLicense()
    navigate('/activate', { replace: true })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 text-3xl mb-4">
          &#9888;
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {isTrial ? 'Trial License Expired' : 'License Expired'}
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          {isTrial
            ? 'Your trial license has ended. Activate a full license to continue using Talent Navigator.'
            : 'Your license has expired. You can still view existing talent navigators in read-only mode, but new generation and export features are disabled.'}
        </p>

        {status.license && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <p className="text-xs text-gray-500 mb-1">Expired license</p>
            <p className="text-sm font-semibold text-gray-800">{status.license.recipientName}</p>
            <p className="text-sm text-gray-500">{status.license.organizationName}</p>
            <p className="text-xs text-red-500 mt-1">
              Expired {new Date(status.license.expiresAt).toLocaleDateString()}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <button
            onClick={handleReactivate}
            className="w-full py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-all"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            Activate New License
          </button>
          {!isTrial && (
            <button
              onClick={async () => {
                await refresh()
                setDismissed(true)
              }}
              className="w-full py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
            >
              View Existing Talent Navigators (Read-Only)
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
